import { getPublishedEssayByIdOptions } from "@/actions/essay/handler";
import { Editor } from "@/components/blocks/editor-essay/editor";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { seo } from "@/utils/seo";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_app/essay/$essayId")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      getPublishedEssayByIdOptions(params.essayId),
    );
    if (data.status === 404) {
      throw notFound();
    }
    return data;
  },
  notFoundComponent: () => {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center pt-28 text-center">
        <h1 className="font-bold text-4xl">Essay Bulunamadı</h1>
        <p className="mt-4 text-muted-foreground">
          Aradığınız içerik mevcut değil, yayınlanmamış veya kaldırılmış
          olabilir.
        </p>
        <Button asChild className="mt-6">
          <Link to="/essay">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tüm İçeriklere Geri Dön
          </Link>
        </Button>
      </div>
    );
  },
  component: EssayDetailPageComponent,
  head: ({ loaderData }) => {
    const essay = loaderData?.body;

    return {
      meta: seo({
        title: essay?.title || "İçerik Bulunamadı",
        description: essay?.metaDescription || essay?.excerpt || essay?.title,
        image: essay?.ogImageUrl || essay?.coverImageUrl || undefined,
        canonical: `https://atalaythecoach.com/essay/${essay?.essayId}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: essay?.title,
            description: essay?.metaDescription || essay?.excerpt,
            image: [essay?.coverImageUrl, essay?.ogImageUrl].filter(Boolean),
            datePublished: essay?.publishedAt
              ? new Date(essay.publishedAt).toISOString()
              : undefined,
            dateModified: essay?.updatedAt
              ? new Date(essay.updatedAt).toISOString()
              : essay?.publishedAt
                ? new Date(essay.publishedAt).toISOString()
                : undefined,
            author: [
              {
                "@type": "Person",
                name: essay?.author?.userName || "Alican Atalay",
                url: "https://atalaythecoach.com",
              },
            ],
            publisher: {
              "@type": "Organization",
              name: "theCoach",
              logo: {
                "@type": "ImageObject",
                url: "https://atalaythecoach.com/favicon/favicon.svg",
              },
            },
          }),
        },
      ],
    };
  },
});

function EssayDetailPageComponent() {
  const { essayId } = Route.useParams();
  const essayQuery = useSuspenseQuery(getPublishedEssayByIdOptions(essayId));

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  if (essayQuery.data.status === 404) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center pt-28 text-center">
        <h1 className="font-bold text-4xl">Essay Bulunamadı</h1>
        <p className="mt-4 text-muted-foreground">
          Aradığınız içerik mevcut değil, yayınlanmamış veya kaldırılmış
          olabilir.
        </p>
        <Button asChild className="mt-6">
          <Link to="/essay">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tüm İçeriklere Geri Dön
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background pt-28 text-foreground sm:pt-32">
      <article className="container mx-auto max-w-2xl px-4 pb-20">
        <header className="mb-10 sm:mb-12">
          <h1 className="font-extrabold font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
            {essayQuery.data.body.title}
          </h1>
          <div className="mt-6 flex items-center gap-2 text-muted-foreground text-sm">
            <span>{formatDate(essayQuery.data.body.publishedAt)}</span>
            {essayQuery.data.body.readTime && (
              <>
                <span>·</span>
                <span>{essayQuery.data.body.readTime}</span>
              </>
            )}
          </div>

          {/* Author Info */}
          {essayQuery.data.body.author && (
            <div className="mt-8 flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarImage
                  src={essayQuery.data.body.author.image || ""}
                  alt={essayQuery.data.body.author.userName}
                />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {essayQuery.data.body.author.userName
                    ?.charAt(0)
                    .toUpperCase() || "A"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-sm">
                  {essayQuery.data.body.author.userName}
                </p>
                <p className="text-muted-foreground text-xs">Yazar</p>
              </div>
            </div>
          )}
        </header>

        {/* Cover Image */}
        {essayQuery.data.body.coverImageUrl && (
          <div className="mb-10 overflow-hidden rounded-lg">
            <img
              src={essayQuery.data.body.coverImageUrl}
              alt={essayQuery.data.body.title}
              className="h-auto w-full object-cover"
            />
          </div>
        )}

        {/* Lexical Content (Read-only) */}
        <Editor
          editorSerializedState={essayQuery.data.body.content as any}
          readOnly={true}
        />
      </article>
    </div>
  );
}
